// v2.0 browser logger. Prior version wrote to error.log via Rust
// `fs_append_log`. Browser has no filesystem; App Insights (via a future
// /api/logs endpoint) will be the durable sink. For now: console-only,
// with the same public surface so callers don't change.
type Level = "DEBUG" | "INFO" | "WARN" | "ERROR";

function formatMeta(meta: unknown): string {
  if (meta === undefined) return "";
  if (meta instanceof Error) {
    const frames = (meta.stack ?? "").split("\n").slice(1, 4).join(" | ");
    return JSON.stringify({ message: meta.message, stack: frames });
  }
  try {
    return JSON.stringify(meta);
  } catch {
    return String(meta);
  }
}

function formatLine(level: Level, source: string, message: string, meta: unknown): string {
  const ts = new Date().toISOString();
  const metaPart = meta !== undefined ? ` | ${formatMeta(meta)}` : "";
  return `${ts} [${level}] ${source} — ${message}${metaPart}`;
}

function write(level: Level, source: string, message: string, meta: unknown) {
  const line = formatLine(level, source, message, meta);
  const args = meta !== undefined ? [line, meta] : [line];
  switch (level) {
    case "DEBUG": console.debug(...args); break;
    case "INFO":  console.info(...args);  break;
    case "WARN":  console.warn(...args);  break;
    case "ERROR": console.error(...args); break;
  }
}

export const log = {
  debug: (source: string, message: string, meta?: unknown) => write("DEBUG", source, message, meta),
  info:  (source: string, message: string, meta?: unknown) => write("INFO",  source, message, meta),
  warn:  (source: string, message: string, meta?: unknown) => write("WARN",  source, message, meta),
  error: (source: string, message: string, meta?: unknown) => write("ERROR", source, message, meta),
};

export function installGlobalErrorHandlers() {
  window.addEventListener("error", (event) => {
    log.error("window.onerror", event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error instanceof Error ? event.error : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    log.error("unhandledrejection", message, reason instanceof Error ? reason : undefined);
  });
}
